/**
 * What a person may say of the homes the add-time check lists, and what then goes to the add
 * (EST-169, slice 3). The half of the check a PAGE needs: it asks the same-home rule nothing
 * and loads nothing, so a page can use it without carrying the rule.
 *
 * The check itself is models/addCheck.js, which re-exports everything here.
 */

/** Why a home is listed, with the words a person reads. */
export const LISTED = Object.freeze({
  'do-not-contact': 'It is, or reads as, a home marked do not contact.',
  'may-be-do-not-contact': 'It cannot be told from a home marked do not contact in its postcode, or shares a number with one.',
  'in-mailshot': 'It reads as a home that is already in this mailshot.',
  'in-selection': 'It reads as another home in this selection, which is kept.',
});
/** The reasons a person may overrule. Do not contact is not one. */
export const RELEASABLE = Object.freeze(['in-mailshot', 'in-selection']);
/** The order that says which of a double is kept, in words, for the page. */
/**
 * The most records of one group that the check works out. A group is the homes of one postcode that read as each
 * other: a bare number and eleven flats of it is twelve records, and is worked out; a bare number and twelve flats is
 * thirteen, and is too large (every home of it goes in, listed for a person's eye). Here, and not beside the check,
 * so that the page can say the number without loading the rule.
 */
export const MAX_GROUP_SEARCHED = 12;

export const KEEP_ORDER_WORDS = 'Of homes in the selection that read as one home, one is kept: a record with a Land Registry address before one without; then the one seen more recently; then the lower id.';

const list = (v) => (Array.isArray(v) ? v : []);

/**
 * The class a listed home is decided in, for "leave all of these out" and "add all of these
 * anyway": its reason, and whether the rule called it the same home or could not tell.
 */
export function classOf(entry) {
  const e = entry || {};
  if (!RELEASABLE.includes(e.why)) return e.why || 'unknown';
  return `${e.why}:${list(e.against).some((a) => a.verdict === 'same') ? 'same' : 'cannot-tell'}`;
}

/**
 * Whether a listed home can be added INSTEAD of the one kept: it is left out against exactly
 * one kept home of the selection, and it is the only home left out against that one.
 * Answers the kept home's id, or null.
 */
export function swapFor(check, entry) {
  const e = entry || {};
  if (e.why !== 'in-selection') return null;
  const kept = list(e.against).filter((a) => a.where === 'selection' && a.kept);
  if (kept.length !== 1 || list(e.against).length !== 1) return null;
  const others = list(check && check.listed).filter((l) => l.id !== e.id && list(l.against).some((a) => a.id === kept[0].id));
  return others.length ? null : kept[0].id;
}

/**
 * What goes to the add, given a check and a person's choices.
 * @param {Object} choices `{ [listed id]: 'add' | 'instead' }`; anything else, or nothing, is "leave it out".
 *   And `{ [id of a home in a group too large to work out]: 'out' }` leaves that home out
 *   (those go in by default); 'out' on any other home does nothing.
 * @returns {{ add: string[], leftOut: string[] }} every home asked of is in exactly one of
 *   `add`, `leftOut` and the check's `alreadyIn`. A do-not-contact home is never in `add`,
 *   whatever is chosen. "instead" adds the listed home and leaves out the one kept in its
 *   place; where that swap is not offered (`swapFor`) it is read as "leave it out".
 */
export function settle(check, choices) {
  const c = check || {}; const chosen = choices && typeof choices === 'object' ? choices : {};
  const add = new Set(list(c.clear).map(String)); const swappedOut = new Set();
  for (const l of list(c.listed)) {
    if (l.releasable !== true || !RELEASABLE.includes(l.why)) continue;
    const choice = chosen[l.id];
    if (choice === 'add') add.add(String(l.id));
    else if (choice === 'instead') { const other = swapFor(c, l); if (other !== null) { add.add(String(l.id)); swappedOut.add(String(other)); } }
  }
  for (const id of swappedOut) add.delete(id);
  // a group too large to work out goes in whole by default; a person may leave any home of it out
  for (const u of list(c.unsearched)) for (const h of list(u.homes)) if (chosen[h.id] === 'out') add.delete(String(h.id));
  const order = [...list(c.clear), ...list(c.listed).map((l) => l.id)].map(String);
  return { add: order.filter((id) => add.has(id)), leftOut: order.filter((id) => !add.has(id)) };
}
